<script setup lang="ts">
import { computed } from 'vue'

import BetaAiGuidancePanel from '@/components/beta-dashboard/organisms/BetaAiGuidancePanel.vue'
import BetaFinancialAlertCard from '@/components/beta-dashboard/molecules/BetaFinancialAlertCard.vue'
import BetaMetricsPanel from '@/components/beta-dashboard/organisms/BetaMetricsPanel.vue'
import BetaProgressPanel from '@/components/beta-dashboard/organisms/BetaProgressPanel.vue'
import BetaRecentPostsPanel from '@/components/beta-dashboard/organisms/BetaRecentPostsPanel.vue'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type {
    BetaAiSuggestion,
    BetaDashboardTask,
    BetaDashboardView,
} from '@/types/beta-dashboard'

const emit = defineEmits<{
    navigate: [view: BetaDashboardView]
    preview: []
    editPost: [postId: number]
    task: [task: BetaDashboardTask]
    suggestion: [suggestion: BetaAiSuggestion]
}>()

const betaStore = useBetaDashboardStore()

const readinessPending = computed(() => (
    !betaStore.contentLoaded && !betaStore.error
))
const showOnboarding = computed(() => (
    betaStore.contentLoaded && betaStore.progress !== 100
))
const sectionOrder = computed(() => (
   showOnboarding.value
        ? ['progress', 'recent', 'suggestions', 'metrics'] as const
        : ['metrics', 'recent', 'suggestions'] as const
))

</script>

<template>
    <div class="beta-overview">
        <section
            v-if="readinessPending"
            class="beta-overview-loading"
            aria-busy="true"
            aria-label="Carregando visão geral"
        >
            <div class="beta-overview-loading__heading" aria-hidden="true">
                <span />
                <span />
            </div>

            <div class="beta-overview-loading__card" aria-hidden="true">
                <span />
                <span />
                <span />
            </div>
        </section>

        <template v-else>
            <BetaFinancialAlertCard @subscriptions="emit('navigate', 'subscriptions')" />

            <header v-if="showOnboarding" class="beta-page-heading">
                <div>
                    <p class="beta-page-heading__eyebrow">Visão geral</p>
                    <h1>Seu site está ganhando forma</h1>
                </div>

                <button type="button" class="beta-page-heading__action" @click="emit('preview')">
                    Visualizar site
                    <span aria-hidden="true">↗</span>
                </button>
            </header>

            <div v-if="betaStore.error" class="beta-inline-error">
                <div>
                    <strong>Alguns dados não puderam ser carregados.</strong>
                    <p>{{ betaStore.error }}</p>
                </div>
                <button type="button" @click="betaStore.load">Tentar novamente</button>
            </div>

            <template v-if="betaStore.contentLoaded">
                <template v-for="section in sectionOrder" :key="section">
                    <BetaProgressPanel
                        v-if="section === 'progress'"
                        data-overview-section="progress"
                        :tasks="betaStore.tasks"
                        :progress="betaStore.progress"
                        :loading="betaStore.loadingContent"
                        @action="emit('task', $event)"
                    />

                    <BetaMetricsPanel
                        v-else-if="section === 'metrics'"
                        data-overview-section="metrics"
                    />

                    <BetaRecentPostsPanel
                        v-else-if="section === 'recent'"
                        data-overview-section="recent"
                        :posts="betaStore.recentPosts"
                        :loading="betaStore.loadingRecentPosts"
                        :error="betaStore.recentPostsError"
                        @browse="emit('navigate', 'content')"
                        @edit="emit('editPost', $event)"
                        @retry="betaStore.loadRecentPosts"
                    />

                    <BetaAiGuidancePanel
                        v-else
                        data-overview-section="suggestions"
                        @action="emit('suggestion', $event)"
                    />
                </template>
            </template>
        </template>
    </div>
</template>

<style scoped>
.beta-overview {
    display: grid;
    gap: 24px;
}

.beta-overview-loading {
    display: grid;
    gap: 24px;
}

.beta-overview-loading__heading,
.beta-overview-loading__card {
    overflow: hidden;
    background: linear-gradient(90deg, #edf1f5 25%, #f7f9fb 50%, #edf1f5 75%);
    background-size: 200% 100%;
    animation: beta-overview-loading 1.3s linear infinite;
}

.beta-overview-loading__heading {
    display: grid;
    width: min(420px, 70%);
    gap: 10px;
    background: none;
}

.beta-overview-loading__heading span,
.beta-overview-loading__card span {
    display: block;
    border-radius: 999px;
    background: #e7ecf2;
}

.beta-overview-loading__heading span:first-child {
    width: 90px;
    height: 10px;
}

.beta-overview-loading__heading span:last-child {
    width: 100%;
    height: 34px;
}

.beta-overview-loading__card {
    display: grid;
    min-height: 300px;
    align-content: center;
    gap: 14px;
    border: 1px solid #e2e7ed;
    border-radius: 24px;
    padding: 32px;
}

.beta-overview-loading__card span {
    width: min(460px, 70%);
    height: 14px;
    background: rgba(255, 255, 255, 0.72);
}

.beta-overview-loading__card span:first-child {
    width: min(300px, 48%);
    height: 22px;
}

.beta-overview-loading__card span:last-child {
    width: min(380px, 60%);
}

@keyframes beta-overview-loading {
    to { background-position: -200% 0; }
}

.beta-page-heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 24px;
}

.beta-page-heading__eyebrow {
    color: var(--beta-accent, var(--main-color, #2f86f6));
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
}

.beta-page-heading h1 {
    margin-top: 8px;
    color: #172132;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 36px;
    font-weight: 600;
    letter-spacing: -0.035em;
    line-height: 1.05;
}

.beta-page-heading p:last-child {
    margin-top: 8px;
    color: #747f8f;
    font-size: 11px;
}

.beta-page-heading__action {
    display: none;
    min-height: 46px;
    align-items: center;
    gap: 12px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 18px;
    color: #fff;
    font-size: 12px;
    font-weight: 850;
}

.beta-inline-error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    border: 1px solid #f4caca;
    border-radius: 18px;
    background: #fff8f8;
    padding: 16px 18px;
    color: #934444;
    font-size: 14px;
}

.beta-inline-error p {
    margin-top: 2px;
    font-size: 11px;
}

.beta-inline-error button {
    flex: none;
    border-radius: var(--beta-button-radius);
    background: #934444;
    padding: 9px 12px;
    color: #fff;
    font-size: 12px;
    font-weight: 800;
}

@media (max-width: 767px) {
    .beta-page-heading {
        align-items: start;
    }

    .beta-page-heading__action {
        display: none;
    }
}
</style>
